import { useMemo, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import ImmediateDigest from "./Widgets/ImmediateDigest";
import NewsAnchorWidget from "./Widgets/NewsAnchorWidget";
import RecentCommercials from "./Widgets/RecentCommercials";
import CVEGraph from "../../Universal/CVEGraph";
import ABHomeTimeline from "./Widgets/ABHomeTimeline";

const IS_CP = process.env.REACT_APP_IS_CP === "true";
const IS_AB = process.env.REACT_APP_IS_AB === "true";

/* ─── Theme ─── */
const getTheme = (dark) =>
  dark
    ? {
        cardBg: "rgba(15,23,42,0.60)",
        cardBorder: "rgba(14,165,233,0.12)",
        textPrimary: "rgba(226,232,240,0.9)",
        textSecondary: "rgba(148,163,184,0.7)",
        textMuted: "rgba(148,163,184,0.4)",
        activeText: "rgba(14,165,233,0.9)",
        activeBorder: "rgba(14,165,233,0.30)",
        activeBg: "rgba(14,165,233,0.15)",
        dangerText: "rgba(248,113,113,0.9)",
      }
    : {
        cardBg: "rgba(241,245,249,0.8)",
        cardBorder: "rgba(203,213,225,0.5)",
        textPrimary: "rgba(15,23,42,0.9)",
        textSecondary: "rgba(71,85,105,0.8)",
        textMuted: "rgba(148,163,184,0.6)",
        activeText: "rgba(2,132,199,1)",
        activeBorder: "rgba(14,165,233,0.30)",
        activeBg: "rgba(14,165,233,0.10)",
        dangerText: "rgba(220,38,38,0.9)",
      };

const panelVariants = {
  hidden: { opacity: 0, filter: "blur(6px)" },
  visible: (i) => ({
    opacity: 1,
    filter: "blur(0px)",
    transition: {
      duration: 0.4,
      delay: i * 0.1,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  }),
};

/* ─── Suggested Topics & Questions ─── */

const ExploreSkeleton = () => (
  <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 py-3">
    <div className="mb-3 shrink-0">
      <div
        className="flex flex-wrap gap-1.5 overflow-hidden"
        style={{ maxHeight: 58 }}
      >
        {[16, 12, 14, 20, 13, 16, 12, 18, 14, 11].map((w, i) => (
          <div
            key={i}
            className="animate-pulse rounded-full bg-surfaceBorder"
            style={{ width: `${w * 4 + 20}px`, height: 24 }}
          />
        ))}
      </div>
    </div>

    <div className="mb-3 shrink-0 h-px bg-mainDivider" />

    <span className="shrink-0 text-[10px] font-medium text-velBlue">
      Suggested Questions
    </span>

    <div className="mt-1.5 flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="shrink-0 animate-pulse rounded-lg border border-surfaceBorder bg-surfaceBorder/30"
          style={{ height: 38 }}
        />
      ))}
    </div>
  </div>
);

const SuggestedTopics = ({ topics, questions }) => {
  const isLoading =
    !topics || !questions || (topics.length === 0 && questions.length === 0);
  const navigate = useNavigate();

  const handleTopicClick = useCallback(
    (topic) => {
      navigate(
        `/chat?message=${encodeURIComponent(`Tell me about ${topic}...`)}`,
      );
    },
    [navigate],
  );

  const handleQuestionClick = useCallback(
    (question) => {
      navigate(`/chat?message=${encodeURIComponent(question)}`);
    },
    [navigate],
  );

  return (
    <div className="flex h-full w-full flex-col overflow-hidden font-poppins font-normal">
      <div className="shrink-0 border-b border-mainDivider px-4 py-2.5">
        <span className="text-xs tracking-wide text-mainText">Explore</span>
      </div>

      {isLoading ? (
        <ExploreSkeleton />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 py-3">
          {topics.length > 0 && (
            <div className="mb-3 shrink-0">
              <div
                className="flex flex-wrap gap-1.5 overflow-hidden"
                style={{ maxHeight: 58 }}
              >
                {topics.map((topic) => (
                  <button
                    key={topic}
                    onClick={() => handleTopicClick(topic)}
                    className="cursor-pointer rounded-full border border-inputBorder bg-transparent px-2.5 py-1 text-[10px] text-mutedText transition-all duration-150 hover:border-velBlue/30 hover:bg-velBlue/10 hover:text-velBlue active:scale-95"
                  >
                    {topic}
                  </button>
                ))}
              </div>
            </div>
          )}

          {topics.length > 0 && questions.length > 0 && (
            <div className="mb-3 shrink-0 h-px bg-mainDivider" />
          )}

          {questions.length > 0 && (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <span className="shrink-0 text-[10px] font-medium text-velBlue">
                Suggested Questions
              </span>

              <div className="mt-1.5 flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
                {questions.slice(0, 3).map((q) => (
                  <button
                    key={q}
                    onClick={() => handleQuestionClick(q)}
                    className="flex flex-1 min-h-0 cursor-pointer items-center overflow-hidden rounded-lg border border-velBlue/15 bg-velBlue/[0.04] px-3 py-2 text-left text-[11px] leading-snug text-mainText transition-all duration-150 hover:border-velBlue/30 hover:bg-velBlue/10 active:scale-[0.99]"
                  >
                    <span className="line-clamp-3">{q}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════
   OverviewView
   ═══════════════════════════════════════════ */
const OverviewView = ({ dark }) => {
  const t = useMemo(() => getTheme(dark), [dark]);
  const [hasVideos, setHasVideos] = useState(true);

  // Suggestions lifted from the digest response
  const [suggestions, setSuggestions] = useState(null);

  const handleSuggestionsReady = useCallback((data) => {
    setSuggestions((prev) => {
      // Don't overwrite existing suggestions with empty data (e.g. force-refresh stream)
      const hasNew = data?.topics?.length > 0 || data?.questions?.length > 0;
      if (prev && !hasNew) return prev;
      return data;
    });
  }, []);

  if (IS_AB) {
    return <ABHomeTimeline dark={dark} t={t} />;
  }

  return (
    <div className="flex h-full w-full gap-3 p-4 font-poppins font-normal text-mainText">
      <motion.div
        className="min-w-0 flex-1"
        custom={0}
        initial="hidden"
        animate="visible"
        variants={panelVariants}
      >
        <div className="h-full overflow-hidden rounded-lg border border-surfaceBorder bg-surface/80 shadow-login-panel backdrop-blur-md">
          <ImmediateDigest
            dark={dark}
            t={t}
            onSuggestionsReady={handleSuggestionsReady}
          />
        </div>
      </motion.div>

      <div className="flex flex-1 min-w-0 flex-col gap-3">
        {hasVideos && (
          <motion.div
            className="shrink-0"
            style={{ maxHeight: "33%" }}
            custom={1}
            initial="hidden"
            animate="visible"
            variants={panelVariants}
          >
            <div
              className="h-full rounded-lg overflow-hidden"
              style={{
                background: t.cardBg,
                border: `1px solid ${t.cardBorder}`,
              }}
            >
              <NewsAnchorWidget
                dark={dark}
                t={t}
                onEmpty={() => setHasVideos(false)}
              />
            </div>
          </motion.div>
        )}

        <motion.div
          className="min-h-0"
          style={{ height: "50%" }}
          custom={2}
          initial="hidden"
          animate="visible"
          variants={panelVariants}
        >
          <div className="h-full overflow-hidden rounded-lg border border-surfaceBorder bg-surface/80 shadow-login-panel backdrop-blur-md">
            <RecentCommercials dark={dark} t={t} />
          </div>
        </motion.div>

        <motion.div
          className="min-h-0"
          style={{ height: "50%" }}
          custom={3}
          initial="hidden"
          animate="visible"
          variants={panelVariants}
        >
          <div className="h-full overflow-hidden rounded-lg border border-surfaceBorder bg-surface/80 shadow-login-panel backdrop-blur-md">
            {IS_CP ? (
              <CVEGraph dark={dark} t={t} />
            ) : (
              <SuggestedTopics
                topics={suggestions?.topics ?? null}
                questions={suggestions?.questions ?? null}
              />
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default OverviewView;
